import React, { Fragment, useEffect, useState } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import ResultsSkeleton from './ResultsSkeleton';
import { mapOrder } from '../../../utils/sort-array';
import CollectionSchema from '../../../components/results/CollectionSchema';
import { dataLayerPushEventActionWithCategory } from '../pdp/utils/utils';
import { getStoreCurrency } from '../../../utils/shopify/getStoreCurrency';
import { GIFTCARDSKU } from '../../../utils/gift-card.js';
import { initWishlist } from '../../../snippets/customer.wishlist';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { useProductsWithSplitFamilies } from '../../../hooks/useProductsWithSplitFamilies/useProductsWithSplitFamilies';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { useContentfulAds } from '@jbhi-fi/components/src/hooks/useContentfulAds';
import { CollectionLeaderboardBanner } from '@jbhi-fi/components/src/CollectionLeaderboardBanner/CollectionLeaderboardBanner';
import { LeaderboardBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/LeaderboardBannerTransformer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { addInsight, postAlgoliaProductClick, postAlgoliaProductView } from '../../../utils/algolia/search-insights';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import { paginateHits } from '../../../utils/algolia/paginateHits';
import { ProductCardVariant } from '@jbhi-fi/components/src/ProductCard/ProductCard';
import { useCartContext } from '../../../utils/cart/context/CartContext';

const ProgressBar = ({ completed }: { completed: number }) => {
  const fillerStyles: React.CSSProperties = {
    height: '100%',
    width: `${completed}%`,
    backgroundColor: '#FFEC0F',
    transition: 'width 1s ease-in-out',
    borderRadius: 'inherit',
    textAlign: 'right',
  };

  return (
    <div className="progress-bar-container">
      <div style={fillerStyles}></div>
    </div>
  );
};

const scrollToTop = (collection: any) => {
  window.scrollTo(0, 0);
  dataLayerPushEventActionWithCategory('plpuparrowclick', 'collection', 'click', collection.handle);
};

interface PageFillerProps {
  hits: Array<AlgoliaProductHit>;
  page: number;
  collection: any;
  preezieFinder: any;
  shouldSplitTiles?: boolean;
  productCardVariant: ProductCardVariant;
  isProductCardDetailsEnabled?: boolean;
  isProductCardDetailBadgesEnabled?: boolean;
}

const PageFiller = ({
  hits,
  page,
  collection,
  preezieFinder,
  shouldSplitTiles,
  productCardVariant,
  isProductCardDetailsEnabled = false,
  isProductCardDetailBadgesEnabled = false,
}: PageFillerProps) => {
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const allHits = useProductsWithSplitFamilies({ hits: hits, shouldSplitTiles: shouldSplitTiles });
  const { contentfulThemeApiClient } = usePublicApiClient();
  const { leaderboards, isLoading } = useContentfulAds({
    queryOptions: { handle: collection.handle },
    contentfulThemeApiClient,
  });
  const { cart } = useCartContext();
  const shouldUseLandscapeCards = productCardVariant === 'landscape';

  return (
    <div className="page-identifier" data-page={page}>
      {allHits.map((hit, i) => {
        const showLeaderboard =
          [12, 24, 35].includes(i) &&
          (page === 1 || !page) &&
          (isLoading || leaderboards) &&
          leaderboards.length > 0 &&
          (leaderboards[i / 12] || (i === 35 && leaderboards[3]));

        return (
          <Fragment key={`${hit.variant_id}${i}`}>
            {i === 4 ? preezieFinder : null}
            {showLeaderboard && i !== 35 ? (
              <CollectionLeaderboardBanner
                {...LeaderboardBannerTransformer.toCollectionLeaderboardBannerProps(leaderboards[i / 12], i / 12)}
                landscapeGrid={shouldUseLandscapeCards}
              />
            ) : null}
            <ProductCard
              details={hit.facets}
              {...AlgoliaHitTransformer.toProductCardProps(hit, {
                trackingDescriptor: {
                  page: 'collection',
                  group: 'collectionProducts',
                  currency: getStoreCurrency(),
                  ecommerceExtraProps: {
                    list: 'collection',
                    position: i + 1,
                  },
                },
              })}
              {...productCartPropsOverrides}
              shouldHideCartOverlay={productCartPropsOverrides.shouldHideCartOverlay || GIFTCARDSKU === hit.sku}
              variant={productCardVariant}
              key={hit.objectID}
              getIsCompared={isAddedToCompare}
              onCompare={compareHandleClick}
              getShouldShowCompare={shouldShowCompare}
              isLoggedIn={isLoggedIn}
              onUpdateWishlist={handleUpdateWishlist}
              getIsInWishlist={getIsInWishlist}
              onClick={() => {
                addInsight(hit.sku, hit.__queryID);
                postAlgoliaProductClick(hit.sku, i + 1, hit.__queryID);
              }}
              onAddToCart={
                onAddToCart
                  ? async (e, sku, variantId, eventData) => {
                      const isSuccess = await onAddToCart(e, hit, variantId, eventData?.trackingDescriptor);
                      if (isSuccess) {
                        addInsight(hit.sku, hit.__queryID, cart);
                      }
                      return isSuccess;
                    }
                  : undefined
              }
              isProductCardDetailsEnabled={isProductCardDetailsEnabled}
              isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
            />

            {showLeaderboard && i === 35 ? (
              <CollectionLeaderboardBanner
                {...LeaderboardBannerTransformer.toCollectionLeaderboardBannerProps(leaderboards[3], 3)}
                landscapeGrid={shouldUseLandscapeCards}
              />
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
};

const MemoizedPageFiller = React.memo(PageFiller);

type InfiniteResultsProps = {
  hits: Array<AlgoliaProductHit>;
  totalResultsCount: number;
  showLoadMore: boolean;
  collection: any;
  hitsPerPage: number;
  curatedSection: any;
  preezieFinder: any;
  isLoading: boolean;
  shouldSplitTiles?: boolean;
  showMore: () => void;
  isLastPage: boolean;
  productCardVariant: ProductCardVariant;
  isProductCardDetailsEnabled?: boolean;
  isProductCardDetailBadgesEnabled?: boolean;
};

const InfiniteResults = ({
  hits,
  totalResultsCount,
  showLoadMore,
  collection,
  hitsPerPage,
  curatedSection,
  preezieFinder,
  isLoading,
  shouldSplitTiles,
  showMore,
  isLastPage,
  productCardVariant,
  isProductCardDetailsEnabled = false,
  isProductCardDetailBadgesEnabled = false,
}: InfiniteResultsProps) => {
  const [trackedSkus, setTrackedSkus] = useState<Array<string>>([]);
  const [hitsPaginated, setHitsPaginated] = useState<Array<Array<AlgoliaProductHit>>>([]);
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const { cart } = useCartContext();

  useEffect(() => {
    const results = hits.map(hit => hit.google_tag_manager);
    if (
      window.gtm.products &&
      results?.[0] &&
      !window.gtm.products.some((p: ProductGtmData) => Number(p.id) === Number(results[0].id))
    ) {
      window.gtm.products = [...window.gtm.products, results[0]];
    } else {
      window.gtm.products = results;
    }

    const paginatedHits = paginateHits(hits, hitsPerPage);

    const untrackedHits: Array<AlgoliaProductHit> = [];
    paginatedHits.forEach(page => {
      page?.forEach(hit => {
        const sku = hit.sku || hit.product?.id;
        if (sku && !trackedSkus.includes(sku)) {
          untrackedHits.push(hit);
          setTrackedSkus(prev => [...prev, sku]);
        }
      });
    });

    if (untrackedHits.length) {
      const chunkSize = 20;
      for (let i = 0; i < untrackedHits.length; i += chunkSize) {
        const chunk = untrackedHits.slice(i, i + chunkSize);
        trackViewItemList({
          currency: getStoreCurrency(),
          listName: 'collection',
          viewedItems: transformAlgoliaHitForTracking(chunk, { listName: 'collection', startIndex: i }),
        });
        postAlgoliaProductView(chunk);
      }
    }

    setHitsPaginated(paginatedHits);
  }, [hits]);

  initWishlist();

  if (curatedSection && hitsPaginated.length) {
    const sortedCuratedContent = mapOrder(hitsPaginated[0], curatedSection, 'sku');

    return (
      <div>
        <div
          className={`collection-results-loop collection-results-loop--${productCardVariant}`}
          data-testid="curated-collection-results-loop"
        >
          <div className="page-identifier">
            {sortedCuratedContent.map((hit: AlgoliaProductHit, i: number) => (
              <Fragment key={`${hit.variant_id}${i}`}>
                {i === 4 ? preezieFinder : null}
                <ProductCard
                  {...AlgoliaHitTransformer.toProductCardProps(hit, {
                    trackingDescriptor: {
                      page: 'curatedCollection',
                      group: 'curatedCollectionProducts',
                      currency: getStoreCurrency(),
                      ecommerceExtraProps: {
                        list: 'curatedCollection',
                        position: i + 1,
                      },
                    },
                  })}
                  {...productCartPropsOverrides}
                  getIsCompared={isAddedToCompare}
                  onCompare={compareHandleClick}
                  shouldHideCartOverlay={productCartPropsOverrides.shouldHideCartOverlay || GIFTCARDSKU === hit.sku}
                  getShouldShowCompare={shouldShowCompare}
                  isLoggedIn={isLoggedIn}
                  onUpdateWishlist={handleUpdateWishlist}
                  getIsInWishlist={getIsInWishlist}
                  onClick={() => {
                    addInsight(hit.sku, hit.__queryID);
                    postAlgoliaProductClick(hit.sku, i + 1, hit.__queryID);
                  }}
                  onAddToCart={
                    onAddToCart
                      ? async (e, sku, variantId, eventData) => {
                          const isSuccess = await onAddToCart(e, hit, variantId, eventData?.trackingDescriptor);
                          if (isSuccess) {
                            addInsight(hit.sku, hit.__queryID, cart);
                          }
                          return isSuccess;
                        }
                      : undefined
                  }
                />
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        className={`collection-results-loop collection-results-loop--${productCardVariant}`}
        data-testid="collection-results-loop"
      >
        {showLoadMore
          ? hitsPaginated.map((pageOfHits, page) => {
              return (
                <MemoizedPageFiller
                  key={page}
                  page={page}
                  hits={pageOfHits}
                  collection={collection}
                  preezieFinder={preezieFinder}
                  shouldSplitTiles={shouldSplitTiles}
                  productCardVariant={productCardVariant}
                  isProductCardDetailsEnabled={isProductCardDetailsEnabled}
                  isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
                />
              );
            })
          : null}
      </div>
      {showLoadMore ? (
        <div className="infinite-hits-loadmore">
          <div className="infinite-hits-text">
            Showing {hits.length} of {totalResultsCount} results
          </div>
          <ProgressBar completed={(hits.length / totalResultsCount) * 100} />
          {isLoading ? <div className="spinner" /> : null}
          {!isLastPage && !isLoading ? (
            <button className="load-more-button" disabled={isLastPage} onClick={showMore}>
              Load more
            </button>
          ) : null}
          <button className="scroll-up-button" onClick={() => scrollToTop(collection)}>
            <div className="arrow-up"></div>
          </button>
        </div>
      ) : null}
      <CollectionSchema hits={hits} collection={collection} totalResultsCount={totalResultsCount} />
    </div>
  );
};

const MemoizedInfiniteResults = React.memo(InfiniteResults);

type CustomInfiniteHitProps = {
  collection: any;
  curatedSection?: any;
  preezieFinder?: any;
  shouldSplitTiles?: boolean;
  showLoadMore?: boolean;
  onHasNoResults?: () => void;
  isMerchStudio?: boolean;
};

const CustomInfiniteHits = ({
  showLoadMore = true,
  collection,
  curatedSection,
  preezieFinder,
  shouldSplitTiles,
  onHasNoResults,
  isMerchStudio,
}: CustomInfiniteHitProps) => {
  const { status, results } = useInstantSearch();
  const { showMore, isLastPage, hits } = useInfiniteHits<AlgoliaProductHit>();
  const nbHits = results?.nbHits ?? 0;
  const hasResults = nbHits > 0;
  const isLoading = status === 'loading' || status === 'stalled';

  const shouldUse2upMobileTiles = useFeatureFlag('shouldUse2upMobileTiles');
  const isHorizontalProductCardEnabled = useFeatureFlag('isHorizontalProductCardEnabled');
  const isProductCardDetailBadgesEnabled = useFeatureFlag('isProductCardDetailBadgesEnabled');
  const isProductCardDetailsEnabled = useFeatureFlag('isProductCardDetailsEnabled');

  const productCardVariant = (() => {
    if (isHorizontalProductCardEnabled) return 'landscape';
    if (shouldUse2upMobileTiles) return 'tablet';
    return 'default';
  })();

  useEffect(() => {
    if (onHasNoResults && !results.__isArtificial && results.nbHits === 0) {
      onHasNoResults();
    }
  }, [results]);

  if (!hasResults && isLoading) {
    return (
      <div
        className={`collection-results-loop collection-results-loop--${productCardVariant}`}
        data-testid="collection-results-loop-skeleton"
      >
        <ResultsSkeleton shouldShimmer variant={productCardVariant} />
      </div>
    );
  }

  if (!hasResults && !isLoading && !isMerchStudio) {
    return (
      <div
        className={`collection-results-loop collection-results-loop--${productCardVariant}`}
        data-testid="collection-results-loop-no-results"
      >
        <div className="no-results">
          <h2>{"Sorry, we weren't able to find any results."}</h2>
          <p>Please try applying less filters, or searching for the item you need</p>
        </div>
        <ResultsSkeleton variant={productCardVariant} />
      </div>
    );
  }

  return (
    <MemoizedInfiniteResults
      hits={hits}
      hitsPerPage={results.hitsPerPage}
      isLoading={isLoading}
      collection={collection}
      totalResultsCount={nbHits}
      showLoadMore={showLoadMore}
      curatedSection={curatedSection}
      preezieFinder={preezieFinder}
      shouldSplitTiles={shouldSplitTiles}
      showMore={showMore}
      isLastPage={isLastPage}
      productCardVariant={productCardVariant}
      isProductCardDetailsEnabled={isProductCardDetailsEnabled}
      isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
    />
  );
};

export default CustomInfiniteHits;
